import { useMemo } from 'react'

import { useLocaleSettings } from './I18nProvider'

/** A date as the API sends it (ISO 8601, UTC), or a `Date`. */
export type DateInput = string | Date

/** Formatters for the viewer's locale and time zone. */
export interface Format {
  /** A date, such as "Oct 7, 2026". A date-only value such as `2026-10-07` is never shifted. */
  readonly date: (value: DateInput, options?: Intl.DateTimeFormatOptions) => string
  /** A time of day, such as "2:30 PM". */
  readonly time: (value: DateInput, options?: Intl.DateTimeFormatOptions) => string
  /** A date and time, such as "Oct 7, 2026, 2:30 PM". */
  readonly dateTime: (value: DateInput, options?: Intl.DateTimeFormatOptions) => string
  /** A number, such as "1,234.5". */
  readonly number: (value: number, options?: Intl.NumberFormatOptions) => string
}

const datePreset: Intl.DateTimeFormatOptions = { dateStyle: 'medium' }
const timePreset: Intl.DateTimeFormatOptions = { timeStyle: 'short' }
const dateTimePreset: Intl.DateTimeFormatOptions = { dateStyle: 'medium', timeStyle: 'short' }

/** Dates, times and numbers through `Intl`, in the viewer's locale and time zone. */
export function useFormat(): Format {
  const { locale, timeZone } = useLocaleSettings()
  return useMemo(() => createFormat(locale, timeZone), [locale, timeZone])
}

/**
 * Builds the formatters for one locale and time zone. A string that is not a date comes back as
 * given, and an invalid `Date` as an empty string, so one bad field does not break a page.
 */
export function createFormat(locale: string, timeZone: string): Format {
  const dateFormats = new Map<string, Intl.DateTimeFormat>()
  const numberFormats = new Map<string, Intl.NumberFormat>()

  function formatDate(value: DateInput, options: Intl.DateTimeFormatOptions): string {
    const parsed = parseDate(value)
    if (parsed === null) {
      return typeof value === 'string' ? value : ''
    }

    // A date-only value names a calendar day, not an instant, so it is shown as written.
    const zone = parsed.isDateOnly ? 'UTC' : timeZone
    const key = JSON.stringify([options, zone])
    let format = dateFormats.get(key)
    if (format === undefined) {
      format = new Intl.DateTimeFormat(locale, { ...options, timeZone: zone })
      dateFormats.set(key, format)
    }
    return format.format(parsed.date)
  }

  return {
    date: (value, options = datePreset) => formatDate(value, options),
    time: (value, options = timePreset) => formatDate(value, options),
    dateTime: (value, options = dateTimePreset) => formatDate(value, options),
    number(value, options = {}) {
      const key = JSON.stringify(options)
      let format = numberFormats.get(key)
      if (format === undefined) {
        format = new Intl.NumberFormat(locale, options)
        numberFormats.set(key, format)
      }
      return format.format(value)
    },
  }
}

const dateOnly = /^\d{4}-\d{2}-\d{2}$/
const withoutOffset = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?$/

function parseDate(value: DateInput): { readonly date: Date; readonly isDateOnly: boolean } | null {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : { date: value, isDateOnly: false }
  }

  // API dates are UTC, but `Date` reads a date and time with no offset as local time.
  const date = new Date(withoutOffset.test(value) ? `${value}Z` : value)
  return Number.isNaN(date.getTime()) ? null : { date, isDateOnly: dateOnly.test(value) }
}
